import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import dayjs from 'dayjs'
import { getNominative } from '../../../common/utils'
import { sizes } from '@lmc/common/constants'
import { gaEvent } from '../../../common/services/analytics'
import {
  Spinner,
  HelpLink,
  CarePlansPaywall,
} from '../../../components/generic'
import CarePlanReportsSection from './components/CarePlanReportsSection'
import { history } from '../../../store'

export class CarePlanReportsDashboard extends PureComponent {
  componentDidMount() {
    const { fetchCarePlans } = this.props
    fetchCarePlans()
    this.gaEvent('Open care_plans_reports')
  }

  gaEvent = (action) =>
    gaEvent({
      category: 'CarePlans',
      action,
    })

  redirectToResidentPage = () => {
    history.push(`/residents?carePlans=true`)
  }

  getMonthlyTotal = (thisMonth) => {
    const names = Object.keys(thisMonth)
    return names.reduce((total, name) => {
      const numberCarePlans = thisMonth[name].length
      return (total += numberCarePlans)
    }, 0)
  }

  sectionHasCarePlans = (section) => !!Object.keys(section).length

  render() {
    const { carePlans, carePlanNominative, preferredPeopleNominative, home } =
      this.props

    if (!home?.features?.carePlans?.enabled || !home?.features?.pro?.enabled) {
      return <CarePlansPaywall />
    }

    if (!carePlans) return <Spinner />

    const { overdue, today, tomorrow, thisWeek, thisMonth } = carePlans

    const hasOverdue = this.sectionHasCarePlans(overdue)
    const hasToday = this.sectionHasCarePlans(today)
    const hasTomorrow = this.sectionHasCarePlans(tomorrow)
    const hasThisWeek = this.sectionHasCarePlans(thisWeek)
    const hasThisMonth = this.sectionHasCarePlans(thisMonth)

    return (
      <div style={styles.wrapper}>
        {!hasOverdue &&
        !hasToday &&
        !hasTomorrow &&
        !hasThisWeek &&
        !hasThisMonth ? (
          <div
            onClick={this.redirectToResidentPage}
            style={styles.emptyMessage}
          >
            {getEmptyMessage(carePlanNominative, preferredPeopleNominative)}
          </div>
        ) : null}
        {hasOverdue && (
          <CarePlanReportsSection
            carePlans={overdue}
            style={styles.section}
            title="Overdue"
            carePlanNominative={carePlanNominative}
          />
        )}
        <CarePlanReportsSection
          carePlans={today}
          showSuccess={!hasOverdue && !hasToday}
          style={styles.section}
          title="Today"
          carePlanNominative={carePlanNominative}
        />
        {hasTomorrow && (
          <CarePlanReportsSection
            carePlans={tomorrow}
            style={styles.section}
            title="Tomorrow"
            carePlanNominative={carePlanNominative}
          />
        )}
        <CarePlanReportsSection
          carePlans={thisWeek}
          style={styles.section}
          title="Next 7 Days"
          carePlanNominative={carePlanNominative}
        />
        {!hasThisWeek && (
          <div style={{ marginBottom: 20 }}>
            <span
              id="due-this-month"
              onClick={() => this.gaEvent('Click care_plans_due_this_month')}
            >
              <strong>{this.getMonthlyTotal(thisMonth)}</strong>
              {` ${getNominative(
                carePlanNominative,
                'Care Plans'
              )} due this month`}
            </span>
          </div>
        )}
        <HelpLink supportLink={SUPPORT_ARTICLE} style={styles.supportLink} />
      </div>
    )
  }
}

const getEmptyMessage = (carePlanNominative, preferredPeopleNominative) => (
  <span>
    Go to a{' '}
    <a>{`${getNominative(preferredPeopleNominative, 'Resident')}'s page`}</a>
    {` to start writing up a ${getNominative(
      carePlanNominative,
      'Care Plan'
    )}.`}
  </span>
)
const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/en/collections/1943707-care-plans-and-assessments'

const styles = {
  emptyMessage: {
    marginBottom: 50,
  },
  section: {
    marginBottom: 24,
  },
  supportLink: {
    display: 'inline-block',
    float: 'left',
  },
  wrapper: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '48px 24px',
  },
}

CarePlanReportsDashboard.propTypes = {
  carePlans: PropTypes.shape({
    today: PropTypes.object.isRequired,
    tomorrow: PropTypes.object.isRequired,
    thisWeek: PropTypes.object.isRequired,
    thisMonth: PropTypes.object.isRequired,
    overdue: PropTypes.object.isRequired,
  }),
  fetchCarePlans: PropTypes.func.isRequired,
  home: PropTypes.object,
}

const mapStateToProps = (state) => ({
  carePlans: Selectors.getCarePlansReportByResident(state),
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  home: state.global.home,
})

const mapDispatchToProps = (dispatch) => ({
  fetchCarePlans: () =>
    dispatch(
      Operations.loadList('care-plans/dashboard', {
        reviewTo: dayjs().add(1, 'months').endOf('day').toISOString(),
        utcOffset: String(dayjs().utcOffset()),
      })
    ),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CarePlanReportsDashboard)
